/**
 * Date utility functions for proper timezone handling
 * These functions ensure UTC dates are correctly converted to local time
 * before calculating relative time differences
 */
import moment from 'moment-timezone';

/**
 * Converts a UTC date string to a local Date object
 * @param dateString - UTC date string (e.g., "2024-01-15T10:30:00Z" or "2024-01-15T10:30:00")
 * @returns Local Date object or null if invalid
 */
export const parseUTCToLocal = (dateString: string): Date | null => {
  try {
    if (!dateString) return null;
    
    let date: Date;
    
    if (dateString.includes('Z') || dateString.includes('+00:00')) {
      // Already in UTC format, parse directly
      date = new Date(dateString);
    } else {
      // Assume it's UTC and append Z if not present
      const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
      date = new Date(utcString);
    }
    
    // Validate the parsed date
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return null;
    }
    
    return date;
  } catch (error) {
    console.error('Error parsing UTC date:', error, 'for date:', dateString);
    return null;
  }
};

/**
 * Formats a date as a relative time string (e.g., "2 hours ago")
 * @param dateString - UTC date string
 * @returns Relative time string
 */
export const formatRelativeTime = (dateString: string): string => {
  try {
    const date = parseUTCToLocal(dateString);
    if (!date) return 'invalid date';
    
    // Get current time in user's local timezone
    const now = new Date();
    
    // Calculate the difference in seconds between local time and converted UTC time
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'min' : 'mins'} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    } else if (diffInSeconds < 31536000) {
      const months = Math.floor(diffInSeconds / 2592000);
      return `${months} ${months === 1 ? 'month' : 'months'} ago`;
    } else {
      const years = Math.floor(diffInSeconds / 31536000);
      return `${years} ${years === 1 ? 'year' : 'years'} ago`;
    }
  } catch (error) {
    console.error('Error formatting relative time:', error, 'for date:', dateString);
    return 'invalid date';
  }
};

/**
 * Standard display format: date only => DD-MMM-YYYY (e.g. 13-Feb-2026).
 * Use for date-only display across AdminHub, modals, etc.
 * @param dateString - UTC or ISO date string
 * @returns Formatted date string or "Invalid date"
 */
export const formatDisplayDate = (dateString: string): string => {
  try {
    const date = parseUTCToLocal(dateString);
    if (!date) return 'Invalid date';
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (error) {
    console.error('Error formatting display date:', error, 'for date:', dateString);
    return 'Invalid date';
  }
};

/**
 * Standard display format: date & time => DD-MMM-YYYY hh:mm A Z (e.g. 13-Feb-2026 08:13 AM IST).
 * Z is the timezone abbreviation (EST, IST, PKT, etc.) from moment-timezone. Use across AdminHub, modals, etc.
 * @param dateString - UTC or ISO date string
 * @returns Formatted date-time string with timezone or "Invalid date"
 */
export const formatDisplayDateTime = (dateString: string): string => {
  try {
    const utc = moment.utc(dateString);
    if (!utc.isValid()) return 'Invalid date';
    const userTz = moment.tz.guess();
    return utc.tz(userTz).format('DD-MMM-YYYY hh:mm A z');
  } catch (error) {
    console.error('Error formatting display date time:', error, 'for date:', dateString);
    return 'Invalid date';
  }
};

/**
 * Formats a date as a localized date string
 * @param dateString - UTC date string
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Localized date string
 */
export const formatLocalDate = (
  dateString: string, 
  options: Intl.DateTimeFormatOptions = {}
): string => {
  try {
    const date = parseUTCToLocal(dateString);
    if (!date) return 'Invalid date';
    
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options
    };
    
    return date.toLocaleDateString(undefined, defaultOptions);
  } catch (error) {
    console.error('Error formatting local date:', error, 'for date:', dateString);
    return 'Invalid date';
  }
};

/**
 * Formats a date as a localized date and time string
 * @param dateString - UTC date string
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Localized date and time string
 */
export const formatLocalDateTime = (
  dateString: string, 
  options: Intl.DateTimeFormatOptions = {}
): string => {
  try {
    const date = parseUTCToLocal(dateString);
    if (!date) return 'Invalid date';
    
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...options
    };
    
    return date.toLocaleString(undefined, defaultOptions);
  } catch (error) {
    console.error('Error formatting local date time:', error, 'for date:', dateString);
    return 'Invalid date';
  }
};

/**
 * Gets the timezone offset in hours for the current user
 * @returns Timezone offset in hours (positive for ahead of UTC, negative for behind)
 */
export const getTimezoneOffset = (): number => {
  return new Date().getTimezoneOffset() / -60;
};

/**
 * Checks if a date string is in the future relative to now
 * @param dateString - UTC date string
 * @returns True if the date is in the future
 */
export const isFutureDate = (dateString: string): boolean => {
  try {
    const date = parseUTCToLocal(dateString);
    if (!date) return false;
    
    return date.getTime() > new Date().getTime();
  } catch (error) {
    console.error('Error checking future date:', error, 'for date:', dateString);
    return false;
  }
};

/**
 * Gets the number of days between a date and now
 * @param dateString - UTC date string
 * @returns Number of days (positive for past dates, negative for future dates)
 */
export const getDaysDifference = (dateString: string): number => {
  try {
    const date = parseUTCToLocal(dateString);
    if (!date) return 0;
    
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  } catch (error) {
    console.error('Error calculating days difference:', error, 'for date:', dateString);
    return 0;
  }
};

/**
 * Formats a date for tooltip display with detailed date/time and relative time
 * @param dateString - UTC date string
 * @returns Formatted string like "Sep 14, 2025, 3:47 AM (1 day ago)"
 */
export const formatDetailedTooltip = (dateString: string): string => {
  try {
    const date = parseUTCToLocal(dateString);
    if (!date) return 'Invalid date';
    
    // Format the detailed date and time
    const detailedDate = date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    // Get the relative time
    const relativeTime = formatRelativeTime(dateString);
    
    // Combine them
    return `${detailedDate} (${relativeTime})`;
  } catch (error) {
    console.error('Error formatting detailed tooltip:', error, 'for date:', dateString);
    return 'Invalid date';
  }
};

/**
 * Checks if two dates are on the same day (ignoring time)
 * @param dateString1 - First UTC date string
 * @param dateString2 - Second UTC date string
 * @returns True if both dates are on the same day
 */
export const isSameDay = (dateString1: string, dateString2: string): boolean => {
  try {
    const date1 = parseUTCToLocal(dateString1);
    const date2 = parseUTCToLocal(dateString2);
    
    if (!date1 || !date2) return false;
    
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  } catch (error) {
    console.error('Error checking same day:', error);
    return false;
  }
};

/**
 * Formats a date for message separator display
 * Returns "Today" for today, "Yesterday" for yesterday, or formatted date like "Aug 08, 2025"
 * @param dateString - UTC date string
 * @returns Formatted date string for separator
 */
export const formatDateSeparator = (dateString: string): string => {
  try {
    const date = parseUTCToLocal(dateString);
    if (!date) return 'Invalid date';
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    // Calculate difference in days
    const diffTime = today.getTime() - messageDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else {
      // Format as "MMM DD, YYYY" (e.g., "Aug 08, 2025")
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
  } catch (error) {
    console.error('Error formatting date separator:', error, 'for date:', dateString);
    return 'Invalid date';
  }
};